import type {
  AssistantAskApiResource,
  AssistantAskResult,
  AssistantConversation,
  AssistantConversationApiResource,
  AssistantMessage,
  AssistantMessageApiResource,
  AssistantMessagePage,
  AssistantMessagePageApiResource,
  AssistantStreamEvent,
  AssistantTurnOutcome,
  AssistantUiAction,
  AssistantUiActionApiResource,
} from './assistantApi.types';
import { ASSISTANT_STREAM_INTERRUPTED_ERROR_CODE } from '@/constants/assistant';

export function mapApiResourceToAssistantConversation(
  resource: AssistantConversationApiResource
): AssistantConversation {
  return {
    id: resource.id,
    title: resource.title,
    lastMessageAt: resource.last_message_at,
    createdAt: resource.created_at,
  };
}

export function mapApiResourceToAssistantUiAction(
  resource: AssistantUiActionApiResource
): AssistantUiAction {
  return {
    actionId: resource.action_id,
    kind: resource.kind,
    destination: resource.destination,
    entityId: resource.entity_id,
    patientTab:
      resource.patient_tab === undefined || resource.patient_tab === null
        ? null
        : resource.patient_tab,
    targets:
      resource.targets === undefined
        ? []
        : resource.targets.map(function mapTarget(target) {
            return { target: target.target, entityId: target.entity_id };
          }),
    caption: resource.caption === undefined || resource.caption === null ? null : resource.caption,
  };
}

export function mapApiResourceToAssistantMessage(
  resource: AssistantMessageApiResource
): AssistantMessage {
  return {
    id: resource.id,
    conversationId: resource.conversation_id,
    sequence: resource.sequence,
    role: resource.role,
    status: resource.status,
    text: resource.text,
    uiActions: resource.ui_actions.map(mapApiResourceToAssistantUiAction),
    outcome: resource.outcome,
    errorCode: resource.error_code,
    feedback: resource.feedback,
    feedbackReason: resource.feedback_reason,
    createdAt: resource.created_at,
  };
}

export function mapApiResourceToAssistantMessagePage(
  resource: AssistantMessagePageApiResource
): AssistantMessagePage {
  return {
    items: resource.items.map(mapApiResourceToAssistantMessage),
    hasMore: resource.has_more,
  };
}

export function mapApiResourceToAssistantAskResult(
  resource: AssistantAskApiResource
): AssistantAskResult {
  return {
    turnId: resource.turn_id,
    userMessage: mapApiResourceToAssistantMessage(resource.user_message),
  };
}

function readText(data: Record<string, unknown>, field: string): string {
  const value = data[field];
  return typeof value === 'string' ? value : '';
}

export function mapServerSentEventToAssistantStreamEvent(
  eventName: string,
  data: Record<string, unknown>
): AssistantStreamEvent | null {
  if (eventName === 'turn_started') {
    return { type: 'turn_started' };
  }
  if (eventName === 'text_delta') {
    return { type: 'text_delta', text: readText(data, 'text') };
  }
  if (eventName === 'tool_activity') {
    return {
      type: 'tool_activity',
      activity: {
        callId: readText(data, 'call_id'),
        activityKey: readText(data, 'activity_key'),
        status: readText(data, 'status') === 'finished' ? 'finished' : 'started',
      },
    };
  }
  if (eventName === 'ui_action') {
    return {
      type: 'ui_action',
      action: mapApiResourceToAssistantUiAction(data as unknown as AssistantUiActionApiResource),
    };
  }
  if (eventName === 'turn_completed') {
    return {
      type: 'turn_completed',
      messageId: readText(data, 'message_id'),
      outcome: readText(data, 'outcome') as AssistantTurnOutcome,
    };
  }
  if (eventName === 'turn_failed') {
    const errorCode = readText(data, 'error_code');
    return {
      type: 'turn_failed',
      errorCode: errorCode.length > 0 ? errorCode : ASSISTANT_STREAM_INTERRUPTED_ERROR_CODE,
    };
  }
  return null;
}
